import {
  SIT_AND_GO_TOURNEYS,
  TOURNEY_ANNOUNCED,
  TOURNEY_STATUS_CANCELLED,
  TOURNEY_STATUS_COMPLETED,
  TOURNEY_STATUS_LIVE,
  TOURNEY_STATUS_MISSED,
  TOURNEY_STATUS_WON,
} from "../../../Poker/data/Constants";
import { useDispatch, useSelector } from "react-redux";
import { updateTourneyFilterSortConfig } from "../../../Poker/redux/slices/lobbySlice";
import { usePlayer } from "../data/useLobby";

export default function useTourneyLobby() {
  const dispatch = useDispatch();
  const player = usePlayer();
  const masterTourneysData = useSelector(
    (state) => state?.lobby?.masterTourneysData
  );
  const tickets = useSelector((state) => state?.lobby?.tourneyTickets);

  // this function to navigate to the tourney details page
  const live = (tourneyIds) => {
    // Get tourney definitions by id
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    // Filter for live or missed tourneys with late reg logic
    const liveTourneysList = tourneys.filter(
      (eachTourney) =>
        (eachTourney?.tourneyStatus === TOURNEY_STATUS_LIVE ||
          eachTourney?.tourneyStatus === TOURNEY_STATUS_MISSED) &&
        (!eachTourney?.lateRegistrationAllowed ||
          eachTourney.lateRegEnded ||
          eachTourney.lateRegistrationStatus === "FINISHED") && 
          eachTourney?.tourneyIdentifier !== 'completedTourneys'
    );

    // Separate SNG tourneys
    const sitAndGoTourneys = [];
    const filteredTourneysList = liveTourneysList.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });

    // Sort by start time and append SNGs at the end
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (currentTourney, nextTourney) =>
    //     currentTourney.tourneyStartTime - nextTourney.tourneyStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];

    // Return only the tourney ids
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  // Returns tourneys starting in the next 2 hours
  const startingInNext2Hours = (tourneyIds) => {
    const now = Date.now();
    const twoHoursLater = now + 2 * 60 * 60 * 1000;
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    const openTourneysList = tourneys.filter(
      (eachTourney) =>
        eachTourney?.tourneyStatus !== TOURNEY_ANNOUNCED &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_LIVE &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_MISSED &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_CANCELLED &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_COMPLETED &&
         eachTourney?.tourneyIdentifier !== 'completedTourneys' &&
        eachTourney.isFeatured === false
    );

    const filteredTourneys = openTourneysList.filter(
      (eachTourney) => eachTourney?.tourneyStartTime * 1000 <= twoHoursLater
    );

    const sitAndGoTourneys = [];
    const filteredTourneysList = filteredTourneys.filter((eachTourney) => {
      if (eachTourney?.tourneyCardA23?.type === "featured") return false;
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });

    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];

    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  // Returns tourneys starting after the next 2 hours
  const startingLater = (tourneyIds) => {
    const now = Date.now();
    const twoHoursLater = now + 2 * 60 * 60 * 1000;
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    const openTourneysList = tourneys.filter(
      (eachTourney) =>
        eachTourney?.tourneyStatus !== TOURNEY_ANNOUNCED &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_LIVE &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_MISSED &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_CANCELLED &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_COMPLETED &&
         eachTourney?.tourneyIdentifier !== 'completedTourneys' &&
        eachTourney.isFeatured === false
    );

    const filteredTourneys = openTourneysList.filter((eachTourney) => {
      return eachTourney?.tourneyStartTime * 1000 >= twoHoursLater;
    });

    const sitAndGoTourneys = [];
    const filteredTourneysList = filteredTourneys.filter((eachTourney) => {
      if (eachTourney?.tourneyCardA23?.type === "featured") return false;
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });

    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];

    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const lateRegistration = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    const lateRegistrationTourneysList = tourneys.filter(
      (eachTourney) =>
        eachTourney?.tourneyStatus === TOURNEY_STATUS_LIVE &&
        eachTourney?.lateRegistrationAllowed &&
        eachTourney?.lateRegistrationStatus !== "FINISHED" &&
        !eachTourney?.lateRegEnded
    );

    // let sortedTourneysList = [...lateRegistrationTourneysList].sort(
    //   (a, b) => a.lateRegistrationDuration - b.lateRegistrationDuration
    // );
    return lateRegistrationTourneysList.map((tourney) => tourney.tourneyId);
  };

  const featured = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    const featuredTourneysList = tourneys.filter(
      (eachTourney) =>
        eachTourney.isFeatured === true &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_LIVE &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_MISSED
    );

    const sitAndGoTourneys = [];
    const filteredTourneysList = featuredTourneysList.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.tourneyStartTime - b.tourneyStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const open = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    const openTourneysList = tourneys.filter(
      (eachTourney) =>
        eachTourney?.tourneyStatus !== TOURNEY_ANNOUNCED &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_LIVE &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_MISSED &&
        eachTourney.isFeatured === false
    );

    const sitAndGoTourneys = [];
    const filteredTourneysList = openTourneysList.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.tourneyStartTime - b.tourneyStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const announced = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    const announcedTourneysList = tourneys.filter(
      (eachTourney) =>
        eachTourney?.tourneyStatus === TOURNEY_ANNOUNCED &&
        !eachTourney.isFeatured
    );

    const sitAndGoTourneys = [];
    const filteredTourneysList = announcedTourneysList.filter((eachTourney) => {
      if (eachTourney?.tourneyCardA23?.type === "featured") return false;
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.registrationStartTime - b.registrationStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];
    //
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const megaTourney = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);

    const announcedTourneysList = tourneys.filter(
      (eachTourney) =>
        (eachTourney?.tourneyStatus === TOURNEY_ANNOUNCED &&
          !eachTourney.isFeatured) ||
        (eachTourney?.tourneyStatus !== TOURNEY_ANNOUNCED &&
          eachTourney?.tourneyStatus !== TOURNEY_STATUS_LIVE &&
          eachTourney?.tourneyStatus !== TOURNEY_STATUS_MISSED &&
          eachTourney?.tourneyStatus !== TOURNEY_STATUS_MISSED &&
          eachTourney?.tourneyStatus !== TOURNEY_STATUS_CANCELLED &&
          eachTourney?.tourneyStatus !== TOURNEY_STATUS_COMPLETED &&
          eachTourney.isFeatured === false)
    );

    const sitAndGoTourneys = [];
    const filteredTourneysList = announcedTourneysList.filter((eachTourney) => {
      if (eachTourney?.tourneyCardA23?.type !== "featured") return false;
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.registrationStartTime - b.registrationStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];
    //
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const wonTourneys = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);
    const wonTourneys = tourneys.filter(
      (eachTourney) => eachTourney?.tourneyStatus === TOURNEY_STATUS_WON
    );
    const sitAndGoTourneys = [];
    const filteredTourneysList = wonTourneys.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.tourneyStartTime - b.tourneyStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const completedTourneys = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);
    const completedTourneys = tourneys.filter(
      (eachTourney) => eachTourney?.tourneyIdentifier === 'completedTourneys' &&
        eachTourney?.tourneyStatus !== TOURNEY_STATUS_CANCELLED
    );
    const sitAndGoTourneys = [];
    const filteredTourneysList = completedTourneys.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.tourneyStartTime - b.tourneyStartTime
    // );

    const cancelledTourneys = tourneys.filter(
      (eachTourney) => eachTourney?.tourneyStatus === TOURNEY_STATUS_CANCELLED
    );
    const sitAndGoTourneysCancelled = [];
    const filteredTourneysListCancelled = cancelledTourneys.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneysCancelled.push(eachTourney);
        return false;
      }
      return true;
    });
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys, ...filteredTourneysListCancelled, ...sitAndGoTourneysCancelled];
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const missedTourneys = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);
    const missedTourneys = tourneys.filter(
      (eachTourney) => eachTourney?.tourneyStatus === TOURNEY_STATUS_MISSED
    );
    const sitAndGoTourneys = [];
    const filteredTourneysList = missedTourneys.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.tourneyStartTime - b.tourneyStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const cancelledTourneys = (tourneyIds) => {
    const tourneys = tourneyIds
      .map((id) => masterTourneysData?.get(id))
      .filter(Boolean);
    const cancelledTourneys = tourneys.filter(
      (eachTourney) => eachTourney?.tourneyStatus === TOURNEY_STATUS_CANCELLED
    );
    const sitAndGoTourneys = [];
    const filteredTourneysList = cancelledTourneys.filter((eachTourney) => {
      if (eachTourney.tournamentType === SIT_AND_GO_TOURNEYS) {
        sitAndGoTourneys.push(eachTourney);
        return false;
      }
      return true;
    });
    // let sortedTourneysList = filteredTourneysList.sort(
    //   (a, b) => a.tourneyStartTime - b.tourneyStartTime
    // );
    let sortedTourneysList = [...filteredTourneysList, ...sitAndGoTourneys];
    return sortedTourneysList.map((tourney) => tourney.tourneyId);
  };

  const requestTourneyFilterSortConfig = (newConfig) => {
    let sortsAndFilters = {
      playerName: player?.userName,
      ...newConfig,
    };
    dispatch(
      updateTourneyFilterSortConfig({ filterSortConfig: sortsAndFilters })
    );
  };

  const ticketTourney = () => {
    let tourneys = tickets.map((ticket) => masterTourneysData?.get(ticket.tourneyId)) || [];
    const notCompletedTourneys = tourneys.filter(
      (eachTourney) => eachTourney?.tourneyIdentifier !== 'completedTourneys'
    );
    return notCompletedTourneys.map((tourney) => tourney.tourneyId);
  };

  return {
    live,
    open,
    startingInNext2Hours,
    startingLater,
    announced,
    lateRegistration,
    featured,
    missedTourneys,
    completedTourneys,
    wonTourneys,
    cancelledTourneys,
    requestTourneyFilterSortConfig,
    megaTourney,
    ticketTourney,
  };
}
